import React from 'react';
import { Resource } from '../../services/resourceService';

interface ResourceStatsProps {
  resources: Resource[];
}

const ResourceStats: React.FC<ResourceStatsProps> = ({ resources }) => {
  // Calculate statistics
  const totalResources = resources.length;
  const activeResources = resources.filter(r => r.is_active).length;
  const totalDownloads = resources.reduce((sum, r) => sum + (r.download_count || 0), 0);
  const totalFiles = resources.reduce((sum, r) => sum + (r.file_count || 0), 0);

  // Category breakdown
  const categoryStats = resources.reduce((acc, resource) => {
    const category = resource.category || 'general';
    if (!acc[category]) {
      acc[category] = { count: 0, downloads: 0 };
    }
    acc[category].count += 1;
    acc[category].downloads += resource.download_count || 0;
    return acc;
  }, {} as Record<string, { count: number; downloads: number }>);

  // Top performing resources
  const topResources = [...resources]
    .sort((a, b) => (b.download_count || 0) - (a.download_count || 0))
    .slice(0, 3);

  const getCategoryColor = (category: string): string => {
    const colors: { [key: string]: string } = {
      'autism': 'bg-blue-100 text-blue-800',
      'adhd': 'bg-green-100 text-green-800',
      'learning-differences': 'bg-purple-100 text-purple-800',
      'speech-language': 'bg-orange-100 text-orange-800',
      'behavioral-support': 'bg-red-100 text-red-800',
      'general': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['general'];
  };

  const getCategoryLabel = (category: string): string => {
    const labels: { [key: string]: string } = {
      'autism': 'Autism Support',
      'adhd': 'ADHD Management',
      'learning-differences': 'Learning Differences',
      'speech-language': 'Speech & Language',
      'behavioral-support': 'Behavioral Support',
      'general': 'General Resources'
    };
    return labels[category] || category;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Overview Stats */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Resources</p>
            <p className="text-2xl font-bold text-gray-900">{totalResources}</p>
            <p className="text-sm text-gray-500">{activeResources} active</p>
          </div>
          <div className="text-3xl">📚</div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Downloads</p>
            <p className="text-2xl font-bold text-gray-900">{totalDownloads.toLocaleString()}</p>
            <p className="text-sm text-gray-500">across all resources</p>
          </div>
          <div className="text-3xl">📥</div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Files</p>
            <p className="text-2xl font-bold text-gray-900">{totalFiles}</p>
            <p className="text-sm text-gray-500">available for download</p>
          </div>
          <div className="text-3xl">📁</div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Avg Downloads</p>
            <p className="text-2xl font-bold text-gray-900">
              {totalResources > 0 ? Math.round(totalDownloads / totalResources) : 0}
            </p>
            <p className="text-sm text-gray-500">per resource</p>
          </div>
          <div className="text-3xl">📊</div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="md:col-span-2 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Resources by Category</h3>
        <div className="space-y-3">
          {Object.entries(categoryStats)
            .sort(([,a], [,b]) => b.count - a.count)
            .map(([category, stats]) => (
              <div key={category} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(category)}`}>
                    {getCategoryLabel(category)}
                  </span>
                  <span className="text-sm text-gray-600">{stats.count} resources</span>
                </div>
                <div className="text-sm text-gray-500">
                  {stats.downloads} downloads
                </div>
              </div>
            ))}
        </div>
        
        {Object.keys(categoryStats).length === 0 && (
          <p className="text-gray-500 text-center py-4">No resources created yet</p>
        )}
      </div>

      {/* Top Performing Resources */}
      <div className="md:col-span-2 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Resources</h3>
        <div className="space-y-3">
          {topResources.map((resource, index) => (
            <div key={resource.id} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  index === 0 ? 'bg-yellow-500' : 
                  index === 1 ? 'bg-gray-400' : 
                  'bg-orange-400'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                    {resource.title}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${getCategoryColor(resource.category)}`}>
                      {getCategoryLabel(resource.category)}
                    </span>
                    {!resource.is_active && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-gray-900">
                  {(resource.download_count || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">downloads</p>
              </div>
            </div>
          ))}
        </div>

        {topResources.length === 0 && (
          <p className="text-gray-500 text-center py-4">No resources with downloads yet</p>
        )}
      </div>
    </div>
  );
};

export default ResourceStats;